import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type SignerView, declineEnvelope, signEnvelope, viewSigner } from '@/api/esign';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

/**
 * Public e-signature page (Epic 226 "utilizable"). Mounted outside the AppShell
 * — the single-use token in /sign/:token is the only credential. Renders a
 * signature-pad canvas; the drawn signature is exported as a data URL and sent
 * to the signer endpoint. A signer may also decline with a reason.
 */
export function PublicSignPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [signer, setSigner] = useState<SignerView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [done, setDone] = useState<'signed' | 'declined' | null>(null);
  const [busy, setBusy] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDecline, setShowDecline] = useState(false);

  useEffect(() => {
    if (!token) return;
    viewSigner(token)
      .then(setSigner)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [token]);

  const padRef = useRef<SignaturePadHandle>(null);

  async function sign() {
    if (!token) return;
    const dataUrl = padRef.current?.toDataURL();
    if (!dataUrl || padRef.current?.isEmpty()) {
      setError(true);
      return;
    }
    setBusy(true);
    try {
      await signEnvelope(token, dataUrl);
      setDone('signed');
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  async function decline() {
    if (!token) return;
    setBusy(true);
    try {
      await declineEnvelope(token, declineReason.trim());
      setDone('declined');
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <Centered>{t('common.loading')}</Centered>;
  if (error && !signer) return <Centered>{t('esign.public.notFound')}</Centered>;
  if (!signer) return <Centered>{t('esign.public.notFound')}</Centered>;
  if (done === 'signed' || signer.signed) return <Centered>{t('esign.public.signed')}</Centered>;
  if (done === 'declined' || signer.declined) return <Centered>{t('esign.public.declined')}</Centered>;

  return (
    <div className="mx-auto min-h-screen max-w-xl px-4 py-12">
      <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">{signer.envelopeTitle}</h1>
      <p className="mt-2 text-sm text-[var(--color-lop-text-secondary)]">
        {t('esign.public.signingAs', { name: signer.signerName, email: signer.signerEmail })}
      </p>

      <div className="mt-6">
        <p className="mb-1 text-sm font-medium text-[var(--color-lop-text-primary)]">{t('esign.public.drawSignature')}</p>
        <SignaturePad ref={padRef} />
        <button
          type="button"
          className="mt-1 text-xs text-[var(--color-lop-text-muted)] hover:underline"
          onClick={() => padRef.current?.clear()}
        >
          {t('esign.public.clear')}
        </button>
      </div>

      <div className="mt-6 flex gap-2">
        <Button onClick={sign} disabled={busy} data-testid="esign-sign">
          {busy ? t('common.saving') : t('esign.public.sign')}
        </Button>
        <Button variant="outline" onClick={() => setShowDecline(true)} disabled={busy}>
          {t('esign.public.decline')}
        </Button>
      </div>

      {showDecline && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-xl">
            <h3 className="mb-3 text-lg font-semibold">{t('esign.public.declineTitle')}</h3>
            <Input
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder={t('esign.public.declineReason')}
              className="mb-4"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowDecline(false)} disabled={busy}>
                {t('common.cancel')}
              </Button>
              <Button size="sm" onClick={decline} disabled={busy}>
                {t('esign.public.decline')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="rounded-lg border bg-card px-8 py-10 text-center text-sm text-[var(--color-lop-text-secondary)] shadow-xl">
        {children}
      </div>
    </div>
  );
}

interface SignaturePadHandle {
  toDataURL: () => string;
  clear: () => void;
  isEmpty: () => boolean;
}

const SignaturePad = forwardRef<SignaturePadHandle>(function SignaturePad(_props, ref) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const dirty = useRef(false);

  const ctx = useCallback(() => canvasRef.current?.getContext('2d') ?? null, []);

  useImperativeHandle(ref, () => ({
    toDataURL: () => canvasRef.current?.toDataURL('image/png') ?? '',
    isEmpty: () => !dirty.current,
    clear: () => {
      const c = canvasRef.current;
      const g = ctx();
      if (c && g) g.clearRect(0, 0, c.width, c.height);
      dirty.current = false;
    },
  }));

  function pos(e: React.PointerEvent) {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }
  function start(e: React.PointerEvent) {
    drawing.current = true;
    const g = ctx();
    if (!g) return;
    const p = pos(e);
    g.beginPath();
    g.moveTo(p.x, p.y);
  }
  function move(e: React.PointerEvent) {
    if (!drawing.current) return;
    const g = ctx();
    if (!g) return;
    const p = pos(e);
    g.lineTo(p.x, p.y);
    g.strokeStyle = '#111';
    g.lineWidth = 2;
    g.lineCap = 'round';
    g.stroke();
    dirty.current = true;
  }
  function end() {
    drawing.current = false;
  }

  return (
    <canvas
      ref={canvasRef}
      width={520}
      height={160}
      className="w-full touch-none rounded-md border border-[var(--color-lop-border)] bg-white"
      onPointerDown={start}
      onPointerMove={move}
      onPointerUp={end}
      onPointerLeave={end}
      data-testid="esign-pad"
    />
  );
});
